// src/components/ui/auth-section-3.jsx
// 21st.dev "auth-section-3" adapted: email+password login only, Grant.ai brand colors,
// wired to the real backend (POST /api/auth/login).
"use client";

import React, { useState } from "react";
import { FlutedGlass } from "@paper-design/shaders-react";
import { Eye, EyeOff, LogIn, AlertCircle, Zap, FileText, Shield, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { login } from "../../api";
import { useTheme } from "../../contexts/ThemeContext";
import Logo from "./Logo";

// WebGL shaders can fail on some machines - fall back to a plain gradient
class ShaderBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,#1B5F6E_0%,#123f49_45%,#050b0d_100%)]" />
      );
    }
    return this.props.children;
  }
}

const features = [
  { icon: Zap, title: "Fast pipeline", text: "Text layer first, vision only for scans" },
  { icon: FileText, title: "Structured fields", text: "Halle, Rechnungsnummer, dates, amounts" },
  { icon: Shield, title: "Nothing stored", text: "Files are processed in memory and discarded" },
];

export default function AuthSectionThree() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Please enter your email and password");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen bg-white p-3 text-black antialiased [font-synthesis:none] dark:bg-[#050505] dark:text-white">
      <div className="grid min-h-[calc(100vh-1.5rem)] gap-6 lg:grid-cols-[0.94fr_1.06fr]">
        {/* Left Side - Login Form */}
        <div className="flex min-h-[640px] items-center justify-center rounded-md border border-black/10 bg-white px-6 py-12 dark:border-white/5 dark:bg-[#0a0a0c] lg:min-h-0 lg:px-14 lg:py-20 xl:px-20">
          <div className="mx-auto w-full max-w-[460px]">
            <div className="flex items-center gap-3">
              <Logo height={30} />
              <span className="text-sm font-semibold tracking-wide text-black/70 dark:text-white/70">
                Verwendungsnachweis
              </span>
            </div>

            <div className="mt-10">
              <h1 className="text-3xl font-medium tracking-tight sm:text-4xl text-black dark:text-white">
                Welcome back
              </h1>
              <p className="mt-2 text-sm text-black/50 dark:text-white/50">
                Sign in with your work email to continue
              </p>
            </div>

            <form className="mt-8 space-y-4" onSubmit={handleSubmit} noValidate>
              <InputField
                label="Email"
                type="email"
                placeholder="name@intuitive-ai.com"
                value={email}
                onChange={setEmail}
                autoComplete="username"
              />

              <InputField
                label="Password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                showPasswordToggle
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
              />

              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3.5 py-2.5 text-[13px] text-red-600 dark:text-red-300">
                  <AlertCircle className="size-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#1B5F6E] to-[#2A8A9E] text-sm font-medium text-white shadow-sm transition-all hover:from-[#17505c] hover:to-[#237a8d] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    <span>Signing in…</span>
                  </>
                ) : (
                  <>
                    <LogIn className="size-4" />
                    <span>Sign in</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Right Side - Brand panel */}
        <div className="relative flex min-h-[560px] flex-col overflow-hidden rounded-md bg-gradient-to-b from-[#0d2b31] via-[#123f49] to-[#1B5F6E] p-8 text-white sm:p-12 lg:min-h-0 lg:p-16">
          {/* Background Shader */}
          <div className="pointer-events-none absolute inset-0 z-0">
            <ShaderBoundary>
              <FlutedGlass
                size={0.89}
                shape="lines"
                angle={0}
                distortionShape="prism"
                distortion={0.5}
                shift={0}
                blur={0}
                edges={0.25}
                stretch={0}
                scale={1.11}
                fit="cover"
                highlights={0.1}
                shadows={0.2}
                grainMixer={0.1}
                grainOverlay={0.1}
                colorBack="#00000000"
                colorHighlight="#FFFFFF"
                colorShadow="#000000"
                className="h-full w-full bg-transparent"
              />
            </ShaderBoundary>
          </div>

          <div className="relative z-10 flex h-full w-full flex-col">
            <div className="max-w-[480px] lg:pt-8">
              <h1 className="text-3xl font-medium tracking-tight sm:text-4xl text-white">
                From PDF to structured data.
              </h1>

              <div className="mt-10 space-y-5">
                {features.map((f) => (
                  <div key={f.title} className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/10 backdrop-blur">
                      <f.icon className="size-5 text-[#8fd4e0]" />
                    </div>
                    <div>
                      <div className="font-semibold leading-tight text-white">{f.title}</div>
                      <div className="mt-0.5 text-xs text-white/60">{f.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InputField({
  label,
  placeholder,
  type = "text",
  value,
  onChange,
  autoComplete,
  showPasswordToggle = false,
  showPassword = false,
  onTogglePassword,
}) {
  const isPassword = type === "password";
  return (
    <div className="w-full space-y-1.5 text-left">
      <label className="text-xs font-semibold text-black/60 dark:text-white/60">
        {label}
      </label>
      <div className="relative flex h-11 items-center rounded-lg border border-black/15 bg-white px-3.5 transition-colors focus-within:border-[#1B5F6E] focus-within:ring-2 focus-within:ring-[#1B5F6E]/20 dark:border-white/10 dark:bg-white/5 dark:focus-within:border-[#2A8A9E]">
        <input
          type={isPassword && showPassword ? "text" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full bg-transparent text-sm text-black outline-none placeholder:text-black/30 dark:text-white dark:placeholder:text-white/30"
        />
        {isPassword && showPasswordToggle && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-3.5 cursor-pointer text-black/40 hover:text-black dark:text-white/40 dark:hover:text-white"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
